import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import debounce from "lodash.debounce";

import {
    Col,
    Form,
    Button,
    FloatingLabel,
    Dropdown,
    ProgressBar,
    OverlayTrigger,
    Tooltip,
    Alert,
    Badge,
    Card,
} from "react-bootstrap";
import {
    FaEye,
    FaEyeSlash,
    FaChevronRight,
    FaChevronLeft,
    FaCheck,
    FaUser,
    FaUserFriends,
    FaExchangeAlt,
    FaQuestionCircle,
    FaInfoCircle,
    FaChevronDown
} from "react-icons/fa";
import { FiCopy, FiDownload, FiCheck, FiDollarSign, FiGrid } from "react-icons/fi";
import { motion } from "framer-motion";
import { toast, ToastContainer } from 'react-toastify';
import { Link, useNavigate } from "react-router-dom";
import logo from "../Images/AZ.png";
import ProductModal from '../Components/Shared/ProductModal';

const SignUp = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [currentSection, setCurrentSection] = useState(0);
    const [hasGuarantor, setHasGuarantor] = useState(false);
    const [showGuarantorPreview, setShowGuarantorPreview] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [products, setProducts] = useState([]);
    const [staff, setStaff] = useState([]);
    const [staffSearch, setStaffSearch] = useState("");
    const [staffAuthStatus, setStaffAuthStatus] = useState(null);
    const [copied, setCopied] = useState(false);
    const [collectionFees, setCollectionFees] = useState([]);
    const [calculatedCollectionFee, setCalculatedCollectionFee] = useState(0);
    const [downPayment, setDownPayment] = useState(0);
    const [showPaymentSummary, setShowPaymentSummary] = useState(true);
    const [showProductModal, setShowProductModal] = useState(false);

    const [formData, setFormData] = useState({
        full_name: "",
        email_address: "",
        phone_number: "",
        date_of_birth: "",
        id_type: "",
        id_number: "",
        passport_number: "",
        bill_number: "",
        emi_plan_duration: "",
        payment_date: "",
        password: "",
        confirm_password: "",
        product_id: "",
        bill_amount: "",
        staff_name: "",
        staff_password: "",
        collection_fee: "",
        down_payment: "",

        guarantor_full_name: "",
        guarantor_email_address: "",
        guarantor_phone_number: "",
        guarantor_date_of_birth: "",
        guarantor_id_type: "",
        guarantor_id_number: "",
        guarantor_passport_number: ""
    });

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await axios.get("https://www.azzenterprises.com/api/products");
                setProducts(res.data);
            } catch (error) {
                toast.error("Failed to load products");
            }
        };

        const fetchStaff = async () => {
            try {
                const res = await axios.get("https://www.azzenterprises.com/api/admin/staff");
                setStaff(res.data);
            } catch (error) {
                toast.error("Failed to load staff");
            }
        };

        const fetchCollectionFees = async () => {
            try {
                const res = await axios.get("https://www.azzenterprises.com/api/fee/collectionfees", {
                    withCredentials: true
                });

                const activeFees = Array.isArray(res.data)
                    ? res.data.filter(fee => fee.status === 1)
                    : [];

                setCollectionFees(activeFees);
            } catch (error) {
                console.error("Failed to load Processing fees", error);
            }
        };

        fetchProducts();
        fetchStaff();
        fetchCollectionFees();
    }, []);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const idTypes = [
        { value: "NRIC", label: "NRIC" },
        { value: "FIN", label: "FIN" },
        { value: "PASSPORT", label: "Passport" }
    ];

    const emiPlans = [
        { value: "3", label: "3 Months" },
        { value: "4", label: "4 Months" },
        { value: "5", label: "5 Months" },
        { value: "6", label: "6 Months" },
        { value: "7", label: "7 Months" },
        { value: "8", label: "8 Months" },
        { value: "9", label: "9 Months" },
        { value: "10", label: "10 Months" },
        { value: "11", label: "11 Months" },
        { value: "12", label: "12 Months" }
    ];

    const generateBillNumber = (phoneNumber, dateOfBirth) => {
        if (!phoneNumber || !dateOfBirth) return "";
        const phoneDigits = phoneNumber.replace(/\D/g, "");
        const lastThreeDigits = phoneDigits.slice(-3);
        const year = new Date(dateOfBirth).getFullYear();
        return `${lastThreeDigits}${year}`;
    };

    const calculateCollectionFee = (amount) => {
        if (!amount || isNaN(amount)) {
            setCalculatedCollectionFee(0);
            return 0;
        }
        const billAmount = parseFloat(amount);
        const applicableFee = collectionFees.find(fee =>
            billAmount >= parseFloat(fee.range_min) &&
            billAmount <= parseFloat(fee.range_max)
        );
        if (applicableFee) {
            const fee = parseFloat(applicableFee.collection_fee);
            setCalculatedCollectionFee(fee);
            return fee;
        } else {
            setCalculatedCollectionFee(0);
            return 0;
        }
    };

    const calculateDownPayment = (billAmount, emiMonths) => {
        if (!billAmount || !emiMonths || isNaN(billAmount) || isNaN(emiMonths)) {
            setDownPayment(0);
            return 0;
        }
        const monthlyEMI = parseFloat(billAmount) / parseInt(emiMonths);
        const totalDownPayment = monthlyEMI;
        setDownPayment(totalDownPayment);
        return totalDownPayment;
    };

    const checkStaffPassword = debounce(async (user_name, password) => {
        try {
            await axios.post("https://www.azzenterprises.com/api/admin/authstaff", { user_name, password });
            setStaffAuthStatus(true);
        } catch {
            setStaffAuthStatus(false);
        }
    }, 700);

    const handleChangeStaff = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === "staff_password" && formData.staff_name && value.trim().length > 2) {
            checkStaffPassword(formData.staff_name, value);
        } else {
            setStaffAuthStatus(null);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if ((name === "phone_number" && formData.date_of_birth) ||
            (name === "date_of_birth" && formData.phone_number)) {
            const billNumber = generateBillNumber(
                name === "phone_number" ? value : formData.phone_number,
                name === "date_of_birth" ? value : formData.date_of_birth
            );
            setFormData(prev => ({ ...prev, bill_number: billNumber }));
        }
        if (name === "bill_amount") {
            const collectionFee = calculateCollectionFee(value);
            if (formData.emi_plan_duration) {
                calculateDownPayment(value, formData.emi_plan_duration);
            }
            setFormData(prev => ({
                ...prev,
                collection_fee: collectionFee.toString()
            }));
        }
        if (name === "emi_plan_duration" && formData.bill_amount) {
            calculateDownPayment(formData.bill_amount, value);
            if (!formData.down_payment || formData.down_payment === "") {
                setFormData(prev => ({
                    ...prev,
                    down_payment: ""
                }));
            }
        }

    };

    const handleGuarantorChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelect = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === "emi_plan_duration" && formData.bill_amount) {
            const downPayment = calculateDownPayment(formData.bill_amount, value);
            setFormData(prev => ({
                ...prev,
                down_payment: downPayment.toString()
            }));
        }
    };

    const handleGuarantorDecision = (decision) => {
        setHasGuarantor(decision);
        setCurrentSection(1);
    };

    const validateSection = (section) => {
        const errors = [];
        if (section === 1) {
            if (!formData.full_name.trim()) errors.push("Full name is required");
            if (!formData.email_address.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errors.push("Invalid email address");
            if (!formData.phone_number.match(/^(?:\+65)?\s?(6|8|9)\d{7}$/)) {
                errors.push("Invalid Singapore phone number");
            }
            if (!formData.date_of_birth) errors.push("Date of birth is required");
        }
        if (section === 2) {
            if (!formData.password || formData.password.length < 8) errors.push("Password must be at least 8 characters");
            if (formData.password !== formData.confirm_password) errors.push("Passwords do not match");
        }
        if (section === 3) {
            if (!formData.id_type) errors.push("ID type is required");
            if (!formData.id_number.trim()) errors.push("ID number is required");
        }
        if (section === 4) {
            if (!formData.bill_number.trim()) errors.push("Bill number is required");
            if (!formData.bill_amount || isNaN(formData.bill_amount) || formData.bill_amount <= 0) {
                errors.push("Bill amount must be a valid number");
            }
            if (!formData.emi_plan_duration) errors.push("EMI plan is required");
            if (!formData.product_id) errors.push("Product selection is required");
            if (!formData.payment_date) errors.push("Payment date is required");
        }
        if (section === 5 && hasGuarantor) {
            if (!formData.guarantor_full_name.trim()) errors.push("Guarantor full name is required");
            if (!formData.guarantor_email_address.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errors.push("Invalid guarantor email address");
            if (!formData.guarantor_phone_number.match(/^(?:\+65)?\s?(6|8|9)\d{7}$/)) {
                errors.push("Invalid guarantor Singapore phone number");
            }
            if (!formData.guarantor_date_of_birth) errors.push("Guarantor date of birth is required");
            if (!formData.guarantor_id_type) errors.push("Guarantor ID type is required");
            if (!formData.guarantor_id_number.trim()) errors.push("Guarantor ID number is required");
        }
        if (section === (hasGuarantor ? 6 : 5)) {
            if (!formData.staff_name) errors.push("Staff selection is required");
            if (!formData.staff_password.trim()) errors.push("Staff password is required");
        }
        if (errors.length > 0) {
            errors.forEach(error => {
                toast.error(error, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            });
            return false;
        }
        return true;
    };

    const handleNext = (e) => {
        e.preventDefault();
        const isValid = validateSection(currentSection);
        if (!isValid) return;
        setCurrentSection(prev => prev + 1);
    };

    const handlePrev = () => {
        if (currentSection === 1) {
            setCurrentSection(0);
        } else {
            setCurrentSection(currentSection - 1);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const finalSection = hasGuarantor ? 6 : 5;
        if (!validateSection(finalSection)) return;

        setIsSubmitting(true);

        try {
            const minDownPayment = calculateDownPayment(formData.bill_amount, formData.emi_plan_duration);
            const customDownPayment = formData.down_payment ? parseFloat(formData.down_payment) : minDownPayment;
            const remainingBalance = parseFloat(formData.bill_amount) - customDownPayment;
            const subsequentMonthly = formData.emi_plan_duration > 1 ?
                (remainingBalance / (Number(formData.emi_plan_duration) - 1)).toFixed(2) : "0.00";

            const userData = {
                ...formData,
                has_guarantor: hasGuarantor,
                collection_fee: calculatedCollectionFee,
                down_payment: customDownPayment,
                monthly_emi: formData.bill_amount && formData.emi_plan_duration
                    ? (Number(formData.bill_amount) / Number(formData.emi_plan_duration)).toFixed(2)
                    : 0,
                subsequent_monthly_emi: subsequentMonthly,
                remaining_balance: remainingBalance
            };

            delete userData.confirm_password;
            delete userData.staff_password;

            const response = await axios.post('https://www.azzenterprises.com/api/customers/signup', userData);

            const successMessage = response.data.hasGuarantor
                ? 'Account created successfully with guarantor! Redirecting to login...'
                : 'Account created successfully! Redirecting to login...';

            toast.success(successMessage, {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                onClose: () => {
                    navigate('/sign-in');
                    setIsSubmitting(false);
                }
            });

        } catch (error) {
            setIsSubmitting(false);
            if (error.response) {
                const resData = error.response.data;
                if (resData.errors) {
                    Object.values(resData.errors).forEach(err => toast.error(err[0]));
                } else if (resData.message) {
                    toast.error(resData.message);
                } else if (resData.error) {
                    toast.error(resData.error);
                } else {
                    toast.error('Registration failed. Please try again.');
                }
            } else if (error.request) {
                toast.error('Network error. Please check your connection.');
            } else {
                toast.error('An unexpected error occurred.');
            }
        }
    };

    const completionPercentage = () => {
        let totalSections = hasGuarantor ? 6 : 5;
        let filledSections = 0;
        if (
            formData.full_name.trim() &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address) &&
            /^[\d\s+-]{8,}$/.test(formData.phone_number) &&
            formData.date_of_birth
        ) {
            filledSections++;
        }
        if (
            formData.password.length >= 8 &&
            formData.password === formData.confirm_password
        ) {
            filledSections++;
        }
        const isIDValid =
            formData.id_type &&
            formData.id_number.trim();
        if (isIDValid) {
            filledSections++;
        }
        const isStep4Valid =
            formData.bill_number.trim() &&
            formData.bill_amount && Number(formData.bill_amount) > 0 &&
            formData.emi_plan_duration &&
            formData.product_id;
        if (isStep4Valid) {
            filledSections++;
        }
        if (hasGuarantor) {
            const isGuarantorValid =
                formData.guarantor_full_name.trim() &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guarantor_email_address) &&
                /^[\d\s+-]{8,}$/.test(formData.guarantor_phone_number) &&
                formData.guarantor_date_of_birth &&
                formData.guarantor_id_type &&
                formData.guarantor_id_number.trim();
            if (isGuarantorValid) {
                filledSections++;
            }
        }
        if (formData.staff_name && staffAuthStatus === true) {
            filledSections++;
        }
        const adjustedTotal = totalSections + 1;
        const adjustedFilled = currentSection > 0 ? filledSections + 1 : filledSections;
        return Math.round((adjustedFilled / adjustedTotal) * 100);
    };

    const getMinDueDate = () => {
        const today = new Date();
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        return nextMonth.toISOString().split('T')[0];
    };

    const generatePreviewText = () => {
        const customerLines = [
            "CUSTOMER INFORMATION",
            `Full Name: ${formData.full_name || "—"}`,
            `Email: ${formData.email_address || "—"}`,
            `Phone: ${formData.phone_number || "—"}`,
            `DOB: ${formData.date_of_birth || "—"}`,
            `ID Type: ${formData.id_type || "—"}`,
            `ID Number: ${formData.id_number || "—"}`,
            formData.passport_number ? `Passport Number: ${formData.passport_number}` : null,
            `Customer Password: ${formData.password || "—"}`,
            "",
            "PRODUCT & BILLING",
            `Bill Number: ${formData.bill_number || "—"}`,
            `Bill Amount: ${formData.bill_amount ? `$${parseFloat(formData.bill_amount).toFixed(2)}` : "—"}`,
            `Processing Fee: $${calculatedCollectionFee.toFixed(2)}`,
            `Down Payment: $${downPayment.toFixed(2)}`,
            `Product: ${products.find(p => p.product_id === formData.product_id)?.product_name || "—"}`,
            `EMI Plan: ${formData.emi_plan_duration ? `${formData.emi_plan_duration} months` : "—"}`,
            `Monthly EMI: ${formData.bill_amount && formData.emi_plan_duration
                ? `$${((Number(formData.bill_amount) / Number(formData.emi_plan_duration)).toFixed(2))}`
                : "—"}`,
            `Due Date: ${formData.payment_date || "—"}`,
            "",
        ].filter(line => line !== null);

        const guarantorLines = hasGuarantor ? [
            "GUARANTOR INFORMATION",
            `Full Name: ${formData.guarantor_full_name || "—"}`,
            `Email: ${formData.guarantor_email_address || "—"}`,
            `Phone: ${formData.guarantor_phone_number || "—"}`,
            `DOB: ${formData.guarantor_date_of_birth || "—"}`,
            `ID Type: ${formData.guarantor_id_type || "—"}`,
            `ID Number: ${formData.guarantor_id_number || "—"}`,
            formData.guarantor_passport_number ? `Passport Number: ${formData.guarantor_passport_number}` : null,
            "",
            `Guarantor Password: ${formData.id_number ? `${formData.id_number}-azz` : "—"}`,
        ].filter(line => line !== null) : [];

        const staffLines = [
            "STAFF ASSIGNMENT",
            `Staff Name: ${formData.staff_name || "—"}`,
            `Staff Auth: ${staffAuthStatus === true ? "Verified ✓" : staffAuthStatus === false ? "Invalid ✗" : "Pending"}`,
            "",
        ];

        const header = [
            `Date: ${new Date().toLocaleDateString()}`,
            `Time: ${new Date().toLocaleTimeString()}`,
            "",
        ];

        const lines = [...header, ...customerLines, ...guarantorLines, ...staffLines];
        return lines.join('\n');
    };

    const handleCopyToClipboard = () => {
        const text = generatePreviewText();
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopied(true);
                toast.success("Preview copied to clipboard!");
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                toast.error("Failed to copy to clipboard");
            });
    };

    const handleDownloadAsTxt = () => {
        const text = generatePreviewText();
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');

        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:]/g, '-');
        const fileName = `customer_preview_${timestamp}.txt`;

        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success("Preview downloaded as .txt file!");
    };

    // const monthlyEMI = React.useMemo(() => {
    //     if (formData.bill_amount && formData.emi_plan_duration) {
    //         return (Number(formData.bill_amount) / Number(formData.emi_plan_duration)).toFixed(2);
    //     }
    //     return "0.00";
    // }, [formData.bill_amount, formData.emi_plan_duration]);

    const minDownPayment = React.useMemo(() => {
        if (formData.bill_amount && formData.emi_plan_duration) {
            return calculateDownPayment(formData.bill_amount, formData.emi_plan_duration);
        }
        return 0;
    }, [formData.bill_amount, formData.emi_plan_duration]);

    const customDownPayment = React.useMemo(() => {
        if (formData.down_payment) {
            return parseFloat(formData.down_payment);
        }
        return minDownPayment;
    }, [formData.down_payment, minDownPayment]);

    const remainingBalance = React.useMemo(() => {
        if (formData.bill_amount && formData.emi_plan_duration) {
            return Number(formData.bill_amount) - customDownPayment;
        }
        return 0;
    }, [formData.bill_amount, formData.emi_plan_duration, customDownPayment]);

    const subsequentMonthly = React.useMemo(() => {
        if (formData.emi_plan_duration && formData.emi_plan_duration > 1) {
            return (remainingBalance / (Number(formData.emi_plan_duration) - 1)).toFixed(2);
        }
        return "0.00";
    }, [formData.emi_plan_duration, remainingBalance]);

    useEffect(() => {
        if (formData.bill_amount) {
            const collectionFee = calculateCollectionFee(formData.bill_amount);
            if (formData.emi_plan_duration) {
                calculateDownPayment(formData.bill_amount, formData.emi_plan_duration);
            }
            setFormData(prev => ({
                ...prev,
                collection_fee: collectionFee.toString()
            }));
        }
    }, [formData.bill_amount, formData.emi_plan_duration]);



    const handleProductSelect = (product) => {
        const productAmount = product.sales_price || "0";

        setFormData(prev => ({
            ...prev,
            product_id: product.product_id,
            bill_amount: productAmount,
            product_name: product.product_name
        }));

        const collectionFee = calculateCollectionFee(productAmount);
        setCalculatedCollectionFee(collectionFee);

        if (formData.emi_plan_duration) {
            const downPayment = calculateDownPayment(productAmount, formData.emi_plan_duration);
            setFormData(prev => ({
                ...prev,
                down_payment: downPayment.toString()
            }));
        }

        setShowProductModal(false);
    };

    const renderSection = () => {
        switch (currentSection) {
            case 0:
                return (
                    <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible">
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                            <div className="text-center mb-6">
                                <div className="d-flex justify-content-center mb-4">
                                    <div className="position-relative">
                                        <div className="rounded-circle bg-primary bg-opacity-10 p-4 d-flex align-items-center justify-content-center"
                                            style={{ width: '100px', height: '100px' }}>
                                            <FaUserFriends size={48} className="text-primary" />
                                        </div>
                                        <div className="position-absolute top-0 start-100 translate-middle bg-white rounded-circle p-1 shadow-sm"
                                            style={{ border: '2px solid #f8f9fa' }}>
                                            <FaQuestionCircle className="text-muted" size={18} />
                                        </div>
                                    </div>
                                </div>
                                <h3 className="fw-semibold mb-3" style={{ fontSize: '1.5rem', color: '#1e293b' }}>
                                    Add a Guarantor?
                                </h3>
                                <p className="text-muted mb-5 mx-auto" style={{ maxWidth: '420px', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                    A guarantor agrees to take responsibility for your EMI payments if needed. This optional step can improve your approval chances.
                                </p>
                            </div>
                        </motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }} className="text-center">
                            <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} className="w-100" style={{ maxWidth: '240px' }}>
                                    <Button variant="primary" size="lg" onClick={() => handleGuarantorDecision(true)} className="w-100 d-flex align-items-center justify-content-center gap-2 py-3" style={{ borderRadius: '12px', background: 'linear-gradient(135deg, #4f46e5 0%,rgb(58, 61, 237) 100%)', border: 'none', fontSize: '1rem', fontWeight: '500', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)' }}>
                                        <FaUserFriends size={20} />
                                        <span>Yes, Add</span>
                                    </Button>
                                    <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.875rem' }}>Recommended for higher limits</p>
                                </motion.div>
                                <div className="d-flex align-items-center justify-content-center my-2 my-md-0">
                                    <div className="position-relative" style={{ width: '40px' }}>
                                        <div className="position-absolute top-50 start-50 translate-middle bg-light rounded-pill" style={{ width: '2px', height: '24px' }}></div>
                                        <span className="position-relative z-1 bg-white px-2 text-muted" style={{ fontSize: '0.875rem', fontWeight: '500' }}>OR</span>
                                    </div>
                                </div>
                                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} className="w-100" style={{ maxWidth: '240px' }}>
                                    <Button variant="outline-secondary" size="lg" onClick={() => handleGuarantorDecision(false)} className="w-100 d-flex align-items-center justify-content-center gap-2 py-3" style={{ borderRadius: '12px', border: '2px solid #e2e8f0', fontSize: '1rem', fontWeight: '500', color: '#475569', background: 'white', transition: 'all 0.2s' }}>
                                        <FaUser size={20} />
                                        <span>No Thanks</span>
                                    </Button>
                                    <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.875rem' }}>Continue without guarantor</p>
                                </motion.div>
                            </div>
                            <div className="mt-3 pt-4">
                                <div className="d-flex flex-column align-items-center gap-2">
                                    <div className="d-flex align-items-center justify-content-center gap-2 text-muted">
                                        <FaInfoCircle size={16} />
                                        <span className="text-muted" style={{ fontSize: '0.875rem' }}>Guarantor can be added later in settings.</span>
                                    </div>
                                    <div className="text-center">
                                        <span className="text-muted" style={{ fontSize: '0.875rem' }}>Already have an account?{' '}
                                            <Link to="/sign-in" className="text-primary fw-bold text-decoration-none" style={{ transition: 'all 0.2s', }}>Sign In</Link>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                );
            case 1:
                return (
                    <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible">
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><h5 className="mb-4">Personal Information</h5></motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="full_name" label="Full Name" className="mb-3"><Form.Control type="text" name="full_name" value={formData.full_name} onChange={handleChange} placeholder="John Doe" /></FloatingLabel></motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="email_address" label="Email" className="mb-3"><Form.Control type="email" name="email_address" value={formData.email_address} onChange={handleChange} placeholder="john@example.com" /></FloatingLabel></motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="phone_number" label="Phone Number (eg: +65 98546526)" className="mb-3"><Form.Control type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder="+65 1234 5678" /></FloatingLabel></motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="date_of_birth" label="Date of Birth" className="mb-4"><Form.Control type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} /></FloatingLabel></motion.div>
                    </motion.div>
                );
            case 2:
                return (
                    <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible">
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><h5 className="mb-4">Account Security</h5></motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                            <FloatingLabel controlId="password" label="Password" className="mb-3">
                                <Form.Control type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} placeholder="Create password" />
                                <Button variant="link" className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted p-0" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <FaEyeSlash /> : <FaEye />}</Button>
                                <Form.Text className="mt-1 text-muted">Must be 8 characters at least</Form.Text>
                            </FloatingLabel>
                        </motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                            <FloatingLabel controlId="confirm_password" label="Confirm Password" className="mb-4">
                                <Form.Control type={showPassword ? "text" : "password"} name="confirm_password" value={formData.confirm_password} onChange={handleChange} placeholder="Confirm password" />
                            </FloatingLabel>
                        </motion.div>
                    </motion.div>
                );
            case 3:
                return (
                    <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible">
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><h5 className="mb-4">Identification Details</h5></motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                            <Form.Group className="mb-3">
                                <Form.Label>ID Type</Form.Label>
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-secondary" id="id-type-dropdown" className="w-100 text-start">{formData.id_type || "Select ID type"}</Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                                        {idTypes.map(type => (<Dropdown.Item key={type.value} onClick={() => handleSelect('id_type', type.value)}>{type.label}</Dropdown.Item>))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                        </motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                            <FloatingLabel controlId="id_number" label="ID Number" className="mb-3">
                                <Form.Control type="text" name="id_number" value={formData.id_number} onChange={handleChange} placeholder="S1234567A" />
                            </FloatingLabel>
                        </motion.div>
                        {formData.id_type === "PASSPORT" && (
                            <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                                <FloatingLabel controlId="passport_number" label="Passport Copy Number (Opt)" className="mb-4">
                                    <Form.Control type="text" name="passport_number" value={formData.passport_number} onChange={handleChange} placeholder="E12345678" />
                                </FloatingLabel>
                            </motion.div>
                        )}
                    </motion.div>
                );
            case 4:
                return (
                    <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible">
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><h5 className="mb-4">Product Information</h5></motion.div>

                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                            <Form.Group className="mb-2">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="mb-0">Select Product</Form.Label>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setShowProductModal(true)}
                                        className="d-flex align-items-center gap-1"
                                        style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                    >
                                        <FiGrid size={14} />
                                    </Button>
                                </div>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        id="product-dropdown"
                                        className="w-100 text-start"
                                        style={{ fontSize: "14px", padding: "0.375rem 0.75rem" }}
                                    >
                                        {formData.product_id ? products.find(p => p.product_id === formData.product_id)?.product_name : "Choose a product"}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100 p-2 dropdown-scroll" style={{ maxHeight: "300px" }}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search product..."
                                            className="mb-2"
                                            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                                            autoFocus
                                            style={{ fontSize: "14px" }}
                                        />
                                        {products.filter((p) => p.product_name.toLowerCase().includes(searchTerm)).map((product) => (
                                            <Dropdown.Item
                                                key={product.product_id}
                                                onClick={() => handleProductSelect(product)}
                                                active={formData.product_id === product.product_id}
                                                style={{ fontSize: "14px" }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center w-100">
                                                    <span>{product.product_name}</span>
                                                    <Badge bg="primary" className="ms-2">
                                                        ${product.sales_price || "0"}
                                                    </Badge>
                                                </div>
                                            </Dropdown.Item>
                                        ))}
                                        {products.filter((p) => p.product_name.toLowerCase().includes(searchTerm)).length === 0 && (
                                            <div className="text-muted px-2 py-1" style={{ fontSize: "14px" }}>No products found</div>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                        </motion.div>

                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }} className="row">
                            <div className="col-md-6">
                                <FloatingLabel controlId="bill_number" label="Bill Number" className="mb-3">
                                    <Form.Control
                                        type="text"
                                        name="bill_number"
                                        value={formData.bill_number}
                                        onChange={handleChange}
                                        placeholder="Auto-generated"
                                        readOnly
                                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                    />
                                </FloatingLabel>
                            </div>
                            <div className="col-md-6">
                                <FloatingLabel controlId="bill_amount" label="Bill Amount ($)" className="mb-3">
                                    <Form.Control
                                        type="number"
                                        name="bill_amount"
                                        value={formData.bill_amount || ""}
                                        onChange={handleChange}
                                        readOnly
                                        placeholder="Enter Bill amount"
                                        min="1"
                                        step="0.01"
                                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                    />
                                </FloatingLabel>
                            </div>
                        </motion.div>

                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }} className="row mb-3">
                            <div className="col-md-6">
                                {formData.bill_amount && !isNaN(formData.bill_amount) && (
                                    <Alert variant="info" className="py-1 px-2 h-100" style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-1">
                                                <FiDollarSign className="text-primary" size={12} />
                                                <span className="fw-medium" style={{ fontSize: '0.8rem' }}>Processing Fee</span>
                                            </div>
                                            <Badge bg="primary" className="px-2 py-1" style={{ fontSize: '0.7rem' }}>${calculatedCollectionFee.toFixed(2)}</Badge>
                                        </div>
                                        {calculatedCollectionFee > 0 && (
                                            <small className="text-muted d-block mt-1" style={{ fontSize: '0.65rem' }}>
                                                Range: ${
                                                    collectionFees.find(fee => parseFloat(formData.bill_amount) >= parseFloat(fee.range_min) && parseFloat(formData.bill_amount) <= parseFloat(fee.range_max))?.range_min || '0'
                                                } - ${
                                                    collectionFees.find(fee => parseFloat(formData.bill_amount) >= parseFloat(fee.range_min) && parseFloat(formData.bill_amount) <= parseFloat(fee.range_max))?.range_max || '0'
                                                }
                                            </small>
                                        )}
                                    </Alert>
                                )}
                            </div>

                            <div className="col-md-6">
                                {formData.bill_amount && formData.emi_plan_duration && (
                                    <div className="h-100">
                                        <div className="mb-1">
                                            <Form.Group>
                                                <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Down Payment ($)</Form.Label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text bg-warning text-dark border-warning" style={{ fontSize: '0.75rem' }}>
                                                        <FiDollarSign size={12} />
                                                    </span>
                                                    <Form.Control
                                                        type="number"
                                                        name="down_payment"
                                                        value={formData.down_payment !== "" ? parseFloat(Number(formData.down_payment).toFixed(2)) : ""}
                                                        onChange={handleChange}
                                                        placeholder="Enter down pay"
                                                        max={formData.bill_amount}
                                                        step="0.01"
                                                        className="py-1"
                                                        style={{ fontSize: '0.8rem' }}
                                                    />
                                                </div>
                                            </Form.Group>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>

                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }} className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-4">
                                    <Form.Label>EMI Plan</Form.Label>
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            variant="outline-secondary"
                                            id="emi-plan-dropdown"
                                            className="w-100 text-start py-1"
                                            style={{ fontSize: "14px" }}
                                        >
                                            {formData.emi_plan_duration ? (() => {
                                                const plan = emiPlans.find(p => p.value === formData.emi_plan_duration);
                                                if (!plan) return "Select EMI plan";
                                                if (formData.down_payment && parseFloat(formData.down_payment) > minDownPayment) {
                                                    return `${plan.label}`;
                                                }

                                                return `${plan.label}`;
                                            })() : "Select EMI plan"}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu
                                            className="w-100 p-1"
                                            style={{ fontSize: "14px", maxHeight: "250px", overflowY: "auto" }}
                                        >
                                            {emiPlans.map(plan => {
                                                return (
                                                    <Dropdown.Item
                                                        key={plan.value}
                                                        onClick={() => handleSelect("emi_plan_duration", plan.value)}
                                                        className="py-1 px-2"
                                                        style={{ fontSize: "14px" }}
                                                    >
                                                        {plan.label}
                                                    </Dropdown.Item>
                                                );
                                            })}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    {formData.down_payment && parseFloat(formData.down_payment) > minDownPayment && (
                                        <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                                            *First month: ${Number(customDownPayment).toFixed(2).replace(/\.00$/, '')} + ${Number(calculatedCollectionFee).toFixed(2).replace(/\.00$/, '')} fee
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-4" >
                                    <Form.Label>Due Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="payment_date"
                                        value={formData.payment_date || ""}
                                        onChange={handleChange}
                                        min={getMinDueDate()}
                                        required
                                        style={{ fontSize: "14px" }}
                                    />
                                </Form.Group>
                            </div>
                        </motion.div>

                        {(formData.bill_amount || formData.emi_plan_duration) && (
                            <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-3">
                                        <div
                                            className="d-flex justify-content-between align-items-center mb-2"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setShowPaymentSummary(!showPaymentSummary)}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <FiDollarSign className="text-primary" size={16} />
                                                <h6 className="fw-bold mb-0" style={{ fontSize: '0.9rem' }}>Payment Summary</h6>
                                            </div>
                                            <motion.div
                                                animate={{ rotate: showPaymentSummary ? 180 : 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <FaChevronDown className="text-muted" size={12} />
                                            </motion.div>
                                        </div>

                                        <motion.div
                                            initial={false}
                                            animate={{
                                                height: showPaymentSummary ? 'auto' : 0,
                                                opacity: showPaymentSummary ? 1 : 0
                                            }}
                                            transition={{ duration: 0.2 }}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            <div className="mt-2" style={{ fontSize: '0.8rem' }}>
                                                <div className="d-flex justify-content-between align-items-center py-1">
                                                    <span className="text-muted">Bill Amount:</span>
                                                    <span className="fw-medium">${parseFloat(formData.bill_amount || 0).toFixed(2)}</span>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center py-1">
                                                    <span className="text-muted">Processing Fee:</span>
                                                    <span className="fw-medium">${calculatedCollectionFee.toFixed(2)}</span>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center py-1">
                                                    <span className="text-muted">First Payment:</span>
                                                    <div className="text-end">
                                                        <div className="fw-medium text-primary">
                                                            ${customDownPayment.toFixed(2)} (Down)
                                                        </div>
                                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                            + ${calculatedCollectionFee.toFixed(2)} (Fee)
                                                        </div>
                                                        <div className="fw-bold">
                                                            ${(customDownPayment + calculatedCollectionFee).toFixed(2)}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center py-1">
                                                    <span className="text-muted">Remaining Balance:</span>
                                                    <span className="fw-medium">${remainingBalance.toFixed(2)}</span>
                                                </div>

                                                {formData.emi_plan_duration > 1 && (
                                                    <div className="d-flex justify-content-between align-items-center py-1">
                                                        <span className="text-muted">
                                                            Subsequent {formData.emi_plan_duration - 1} {formData.emi_plan_duration - 1 === 1 ? 'month' : 'months'}:
                                                        </span>
                                                        <span className="fw-medium">${subsequentMonthly}/month</span>
                                                    </div>
                                                )}

                                                <div className="d-flex justify-content-between align-items-center py-1 pt-2 border-top">
                                                    <span className="text-muted fw-medium">Total to Pay:</span>
                                                    <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>
                                                        ${(parseFloat(formData.bill_amount || 0) + calculatedCollectionFee).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        </motion.div>

                                        {!showPaymentSummary && (
                                            <div className="d-flex justify-content-between align-items-center mt-2 pt-1 border-top">
                                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>First Payment:</small>
                                                <Badge bg="primary" className="px-2 py-1" style={{ fontSize: '0.75rem' }}>
                                                    ${(customDownPayment + calculatedCollectionFee).toFixed(2)}
                                                </Badge>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        )}
                    </motion.div>
                );
            case 5:
                if (!hasGuarantor) {
                    return (
                        <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible">
                            <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><h5 className="mb-4">Staff Authentication</h5></motion.div>
                            <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                                <Form.Group className="mb-4">
                                    <Form.Label>Select Staff</Form.Label>
                                    <Dropdown>
                                        <Dropdown.Toggle variant="outline-secondary" id="staff-dropdown" className="w-100 text-start">{formData.staff_name || "Choose a staff"}</Dropdown.Toggle>
                                        <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                                            <Form.Control type="text" placeholder="Search staff" className="mb-2" onChange={(e) => setStaffSearch(e.target.value.toLowerCase())} autoFocus />
                                            {staff.filter(s => s.user_name.toLowerCase().includes(staffSearch)).map((s, idx) => (<Dropdown.Item key={idx} onClick={() => handleSelect("staff_name", s.user_name)} active={formData.staff_name === s.user_name}>{s.user_name}</Dropdown.Item>))}
                                            {staff.filter(s => s.user_name.toLowerCase().includes(staffSearch)).length === 0 && (<div className="text-muted px-2 py-1">No staff found</div>)}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Form.Group>
                            </motion.div>
                            <FloatingLabel controlId="staff_password" label="Staff Password" className="mb-4 position-relative">
                                <Form.Control type="password" name="staff_password" value={formData.staff_password} onChange={handleChangeStaff} placeholder="Enter staff password" isInvalid={staffAuthStatus === false} isValid={staffAuthStatus === true} style={{ boxShadow: staffAuthStatus === true ? "0 0 8px rgba(0, 200, 0, 0.6)" : staffAuthStatus === false ? "0 0 8px rgba(255, 0, 0, 0.6)" : "none", borderColor: staffAuthStatus === true ? "limegreen" : staffAuthStatus === false ? "red" : undefined, }} />
                            </FloatingLabel>
                        </motion.div>
                    );
                }
                return (
                    <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible">
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><h5 className="mb-4">Guarantor Details</h5></motion.div>
                        <div className="row">
                            <div className="col-md-6">
                                <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="guarantor_full_name" label="Full Name" className="mb-3"><Form.Control type="text" name="guarantor_full_name" value={formData.guarantor_full_name} onChange={handleGuarantorChange} placeholder="Jane Smith" /></FloatingLabel></motion.div>
                                <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="guarantor_email_address" label="Email" className="mb-3"><Form.Control type="email" name="guarantor_email_address" value={formData.guarantor_email_address} onChange={handleGuarantorChange} placeholder="jane@example.com" /></FloatingLabel></motion.div>
                                <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="guarantor_phone_number" label="Phone Number" className="mb-3"><Form.Control type="tel" name="guarantor_phone_number" value={formData.guarantor_phone_number} onChange={handleGuarantorChange} placeholder="+65 8765 4321" /></FloatingLabel></motion.div>
                                <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="guarantor_date_of_birth" label="Date of Birth" className="mb-4"><Form.Control type="date" name="guarantor_date_of_birth" value={formData.guarantor_date_of_birth} onChange={handleGuarantorChange} /></FloatingLabel></motion.div>
                            </div>
                            <div className="col-md-6">
                                <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>ID Type</Form.Label>
                                        <Dropdown>
                                            <Dropdown.Toggle variant="outline-secondary" id="guarantor-id-type-dropdown" className="w-100 text-start">{formData.guarantor_id_type || "Select ID type"}</Dropdown.Toggle>
                                            <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                                                {idTypes.map(type => (<Dropdown.Item key={type.value} onClick={() => handleSelect('guarantor_id_type', type.value)}>{type.label}</Dropdown.Item>))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Form.Group>
                                </motion.div>
                                <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="guarantor_id_number" label="ID Number" className="mb-3"><Form.Control type="text" name="guarantor_id_number" value={formData.guarantor_id_number} onChange={handleGuarantorChange} placeholder="S7654321A" /></FloatingLabel></motion.div>
                                {formData.guarantor_id_type === "PASSPORT" && (
                                    <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><FloatingLabel controlId="guarantor_passport_number" label="Guarantor Passport Copy Number (Opt)" className="mb-4"><Form.Control type="text" name="guarantor_passport_number" value={formData.guarantor_passport_number} onChange={handleGuarantorChange} placeholder="E87654321" /></FloatingLabel></motion.div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                );
            default:
                return (
                    <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible">
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}><h5 className="mb-4">Staff Authentication</h5></motion.div>
                        <motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}>
                            <Form.Group className="mb-4">
                                <Form.Label>Select Staff</Form.Label>
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-secondary" id="staff-dropdown" className="w-100 text-start">{formData.staff_name || "Choose a staff"}</Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                                        <Form.Control type="text" placeholder="Search staff" className="mb-2" onChange={(e) => setStaffSearch(e.target.value.toLowerCase())} autoFocus />
                                        {staff.filter(s => s.user_name.toLowerCase().includes(staffSearch)).map((s, idx) => (<Dropdown.Item key={idx} onClick={() => handleSelect("staff_name", s.user_name)} active={formData.staff_name === s.user_name}>{s.user_name}</Dropdown.Item>))}
                                        {staff.filter(s => s.user_name.toLowerCase().includes(staffSearch)).length === 0 && (<div className="text-muted px-2 py-1">No staff found</div>)}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                        </motion.div>
                        <FloatingLabel controlId="staff_password" label="Staff Password" className="mb-4 position-relative">
                            <Form.Control type="password" name="staff_password" value={formData.staff_password} onChange={handleChangeStaff} placeholder="Enter staff password" isInvalid={staffAuthStatus === false} isValid={staffAuthStatus === true} style={{ boxShadow: staffAuthStatus === true ? "0 0 8px rgba(0, 200, 0, 0.6)" : staffAuthStatus === false ? "0 0 8px rgba(255, 0, 0, 0.6)" : "none", borderColor: staffAuthStatus === true ? "limegreen" : staffAuthStatus === false ? "red" : undefined, }} />
                        </FloatingLabel>
                    </motion.div>
                );
        }
    };

    const getPreviewContent = () => {
        if (showGuarantorPreview && hasGuarantor) {
            return {
                title: "Guarantor Preview",
                sections: [
                    {
                        title: "Personal Information",
                        data: [
                            { label: "Full Name", value: formData.guarantor_full_name || "—" },
                            { label: "Email", value: formData.guarantor_email_address || "—" },
                            { label: "Phone", value: formData.guarantor_phone_number || "—" },
                            { label: "DOB", value: formData.guarantor_date_of_birth || "—" }
                        ]
                    },
                    {
                        title: "Product Details",
                        data: [
                            { label: "Product", value: products.find(p => p.product_id === formData.product_id)?.product_name || "—" },
                            { label: "Bill No", value: formData.bill_number || "—" },
                            { label: "Bill Amount", value: formData.bill_amount ? `$${parseFloat(formData.bill_amount).toFixed(2)}` : "—" },
                            { label: "Processing Fee", value: `$${calculatedCollectionFee.toFixed(2)}` },
                            { label: "Down Payment", value: `$${downPayment.toFixed(2)}` },
                            { label: "EMI Plan", value: formData.emi_plan_duration ? `${formData.emi_plan_duration} months` : "—" },
                            { label: "Monthly EMI", value: formData.bill_amount && formData.emi_plan_duration ? `$${((Number(formData.bill_amount) / Number(formData.emi_plan_duration)).toFixed(2))}` : "—" },
                            { label: "Due Date", value: formData.payment_date || "—" }
                        ]
                    },
                    {
                        title: "Identification",
                        data: [
                            { label: "ID Type", value: formData.guarantor_id_type || "—" },
                            { label: "ID Number", value: formData.guarantor_id_number || "—" },
                            ...(formData.guarantor_passport_number ? [{ label: "Passport", value: formData.guarantor_passport_number }] : [])
                        ]
                    },
                ]
            };
        }
        return {
            title: "Customer Preview",
            sections: [
                {
                    title: "Personal Information",
                    data: [
                        { label: "Full Name", value: formData.full_name || "—" },
                        { label: "Email", value: formData.email_address || "—" },
                        { label: "Phone", value: formData.phone_number || "—" },
                        { label: "DOB", value: formData.date_of_birth || "—" }
                    ]
                },
                {
                    title: "Product Details",
                    data: [
                        { label: "Product", value: products.find(p => p.product_id === formData.product_id)?.product_name || "—" },
                        { label: "Bill No", value: formData.bill_number || "—" },
                        { label: "Bill Amount", value: formData.bill_amount ? `$${parseFloat(formData.bill_amount).toFixed(2)}` : "—" },
                        { label: "Processing Fee", value: `$${calculatedCollectionFee.toFixed(2)}` },
                        { label: "Down Payment", value: `$${downPayment.toFixed(2)}` },
                        { label: "EMI Plan", value: formData.emi_plan_duration ? `${formData.emi_plan_duration} months` : "—" },
                        { label: "Monthly EMI", value: formData.bill_amount && formData.emi_plan_duration ? `$${((Number(formData.bill_amount) / Number(formData.emi_plan_duration)).toFixed(2))}` : "—" },
                        { label: "Due Date", value: formData.payment_date || "—" }
                    ]
                },
                {
                    title: "Identification",
                    data: [
                        { label: "ID Type", value: formData.id_type || "—" },
                        { label: "ID Number", value: formData.id_number || "—" },
                        ...(formData.passport_number ? [{ label: "Passport", value: formData.passport_number }] : [])
                    ]
                }
            ]
        };
    };

    const previewContent = getPreviewContent();
    const hasPreviewData = formData.full_name || formData.email_address || formData.product_id;

    const renderPreviewIcon = () => {
        if (!hasGuarantor) return null;
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip>Toggle Customer/Guarantor</Tooltip>}>
                <Button variant="outline-light" size="sm" onClick={() => setShowGuarantorPreview(!showGuarantorPreview)} className="d-flex align-items-center justify-content-center ms-2" style={{ padding: '4px 12px', border: '1px solid rgba(255, 255, 255, 0.4)', minWidth: '36px', minHeight: '36px' }}>
                    <FaExchangeAlt size={14} />
                </Button>
            </OverlayTrigger>
        );
    };

    return (
        <div className="d-flex min-vh-100">
            <Helmet><title>Sign Up | Customers</title><style>{`.loading-overlay {position: fixed; top: 0; left: 0; z-index: 1050; width: 100vw; height: 100vh; background-color: rgba(255, 255, 255, 0.7); display: flex; justify-content: center; align-items: center;}`}</style></Helmet>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover draggable progress={undefined} />
            {isSubmitting && (<div className="loading-overlay"><div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}><span className="visually-hidden">Loading...</span></div></div>)}
            <Col md={6} className="d-none d-md-flex flex-column justify-content-center align-items-center p-5" style={{ background: 'linear-gradient(135deg, #221c8bff 0%,rgba(255, 89, 172, 1) 100%)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.1)' }}></div>
                <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.1)' }}></div>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, }}>
                    {currentSection === 0 && products.map((product, index) => {
                        const hue = (index * 30) % 360;
                        const bgColor = `hsla(${hue}, 80%, 50%, 0.15)`;
                        const borderColor = `hsla(${hue}, 80%, 60%, 0.4)`;
                        const textColor = `hsl(${hue}, 80%, 85%)`;
                        return (
                            <motion.div key={product.product_id} initial={{ y: -100, x: `${10 + (index * 10)}%`, opacity: 0, rotate: Math.random() * 20 - 10 }} animate={{ y: '100vh', opacity: [0, 0.7, 0.7, 0], rotate: Math.random() * 360 }} transition={{ duration: 8 + Math.random() * 4, delay: index * 0.5, repeat: Infinity, ease: "linear" }} style={{ position: 'absolute', fontSize: '0.9rem', fontWeight: '600', color: textColor, whiteSpace: 'nowrap', textShadow: '0 2px 4px rgba(0,0,0,0.4)', padding: '5px 14px', background: `linear-gradient(135deg, ${bgColor}, rgba(255, 255, 255, 0.08))`, borderRadius: '20px', backdropFilter: 'blur(3px)', border: `1px solid ${borderColor}`, textTransform: 'none', boxShadow: `0 4px 10px hsla(${hue}, 80%, 50%, 0.2)` }}>{product.product_name}</motion.div>
                        );
                    })}
                </div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center w-100" style={{ zIndex: 1, maxWidth: "500px" }}>
                    <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity }}><img src={logo} alt="Dashboard UI" className="img-fluid mb-4" style={{ maxWidth: "80px" }} /></motion.div>
                    {currentSection === 0 ? (
                        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="w-100">
                            <motion.div initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, type: "spring", stiffness: 100 }}><h2 className="text-white mb-4 fw-bold display-6">Hello & welcome</h2></motion.div>
                            <motion.p initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.1, type: "spring", stiffness: 90 }} className="text-white-50 mb-5 lead" style={{ fontSize: '16px' }}>Start your financial journey with us</motion.p>
                            <div className="position-relative" style={{ height: '200px' }}>
                                <motion.div initial={{ y: -300, opacity: 0, rotate: -10 }} animate={{ y: 0, opacity: 1, rotate: 0 }} transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 120, damping: 10 }} className="position-absolute" style={{ left: '10%', width: '120px', height: '120px', background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)', borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}><div className="d-flex flex-column justify-content-center align-items-center h-100"><FaUser size={32} className="text-white mb-2" /><span className="text-white fw-medium" style={{ fontSize: '0.85rem' }}>Customer</span></div></motion.div>
                                <motion.div initial={{ y: -400, opacity: 0, rotate: 10 }} animate={{ y: 0, opacity: 1, rotate: 0 }} transition={{ duration: 0.8, delay: 0.5, type: "spring", stiffness: 120, damping: 10 }} className="position-absolute" style={{ left: '50%', transform: 'translateX(-50%)', width: '140px', height: '140px', background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)', borderRadius: '25px', backdropFilter: 'blur(10px)', border: '2px solid rgba(255,255,255,0.3)', boxShadow: '0 15px 35px rgba(0,0,0,0.3)', zIndex: 2 }}><div className="d-flex flex-column justify-content-center align-items-center h-100"><FaQuestionCircle size={40} className="text-white mb-2" /><span className="text-white fw-bold" style={{ fontSize: '0.9rem' }}>Guarantor?</span></div></motion.div>
                                <motion.div initial={{ y: -350, opacity: 0, rotate: -5 }} animate={{ y: 0, opacity: 1, rotate: 0 }} transition={{ duration: 0.8, delay: 0.4, type: "spring", stiffness: 120, damping: 10 }} className="position-absolute" style={{ right: '10%', width: '120px', height: '120px', background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)', borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}><div className="d-flex flex-column justify-content-center align-items-center h-100"><FaUserFriends size={32} className="text-white mb-2" /><span className="text-white fw-medium" style={{ fontSize: '0.85rem' }}>Guarantor</span></div></motion.div>
                                <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.8 }} style={{ position: 'absolute', top: '60px', left: '130px', right: '130px', height: '2px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', transformOrigin: 'left center' }} />
                            </div>
                            <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, delay: 1 }} className="mt-5 pt-4"><p className="text-white-50 mb-0" style={{ fontSize: '0.95rem' }}><FaInfoCircle className="me-2" />Add a guarantor for better approval chances</p></motion.div>
                        </motion.div>
                    ) : (<>
                        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }}><h2 className="text-white mb-3 fw-bold">{currentSection === 0 ? "Welcome!" : `Welcome, ${formData.full_name || "User"}`}</h2></motion.div>
                        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.1 }}><p className="text-white-50 mb-4">{currentSection === 0 ? "Enter your details to get started" : formData.email_address ? <>We'll contact you at <b>{formData.email_address}</b></> : "Enter your details to get started"}{currentSection > 0 && hasGuarantor && (<motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="d-block mt-1"><FaUserFriends className="me-2" size={14} />This registration includes a guarantor</motion.span>)}</p></motion.div>
                        {currentSection > 0 && (<motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="live-preview text-start">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                    <motion.h6 initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="text-white mb-0 me-3">{previewContent.title}</motion.h6>
                                </div>
                                {hasPreviewData && (<motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="d-flex gap-2">
                                    {hasGuarantor && renderPreviewIcon()}
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Copy preview to clipboard</Tooltip>}>
                                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                            <Button variant="outline-light" size="sm" onClick={handleCopyToClipboard} className="d-flex align-items-center gap-1" style={{ padding: '4px 12px', minWidth: '36px', minHeight: '36px' }}>{copied ? <FiCheck /> : <FiCopy />}</Button>
                                        </motion.div>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Download preview as .txt file</Tooltip>}>
                                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                            <Button variant="outline-light" size="sm" onClick={handleDownloadAsTxt} className="d-flex align-items-center gap-1" style={{ padding: '4px 12px', minWidth: '36px', minHeight: '36px' }}><FiDownload /></Button>
                                        </motion.div>
                                    </OverlayTrigger>
                                </motion.div>)}
                            </div>
                            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, delay: 0.5 }} style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', padding: '15px', borderRadius: '8px', maxHeight: '500px', overflowY: 'auto' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px 20px', color: '#f0f0f0', fontSize: '0.8rem' }}>
                                    <div>{previewContent.sections.slice(0, Math.ceil(previewContent.sections.length / 2)).map((section, sectionIndex) => (<motion.div key={sectionIndex} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + (sectionIndex * 0.1) }} style={{ marginBottom: sectionIndex < Math.ceil(previewContent.sections.length / 2) - 1 ? '16px' : '0' }}>
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 + (sectionIndex * 0.1) }} style={{ fontWeight: '600', color: '#fff', marginBottom: '8px', fontSize: '0.85rem' }}>{section.title}</motion.div>
                                        {section.data.map((item, itemIndex) => (<motion.div key={itemIndex} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 + (sectionIndex * 0.1) + (itemIndex * 0.05) }} style={{ marginBottom: '4px' }}><strong>{item.label}:</strong> {item.value}</motion.div>))}
                                    </motion.div>))}</div>
                                    <div>{previewContent.sections.slice(Math.ceil(previewContent.sections.length / 2)).map((section, sectionIndex) => (<motion.div key={sectionIndex} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + (sectionIndex * 0.1) }} style={{ marginBottom: '16px' }}>
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 + (sectionIndex * 0.1) }} style={{ fontWeight: '600', color: '#fff', marginBottom: '8px', fontSize: '0.85rem' }}>{section.title}</motion.div>
                                        {section.data.map((item, itemIndex) => (<motion.div key={itemIndex} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 + (sectionIndex * 0.1) + (itemIndex * 0.05) }} style={{ marginBottom: '4px' }}><strong>{item.label}:</strong> {item.value}</motion.div>))}
                                    </motion.div>))}
                                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} style={{ marginBottom: '16px' }}>
                                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }} style={{ fontWeight: '600', color: '#fff', marginBottom: '8px', fontSize: '0.85rem' }}>Staff & Status</motion.div>
                                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} style={{ marginBottom: '4px' }}><strong>Staff:</strong> {formData.staff_name || "—"}</motion.div>
                                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} style={{ marginBottom: '4px', color: staffAuthStatus === true ? '#4ade80' : staffAuthStatus === false ? '#f87171' : '#fbbf24' }}><strong>Status:</strong> {staffAuthStatus === true ? "✓ Verified" : staffAuthStatus === false ? "✗ Invalid" : "Pending"}</motion.div>
                                        </motion.div>
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} style={{ fontWeight: '600', color: '#fff', marginBottom: '8px', fontSize: '0.7rem', opacity: 0.8, paddingTop: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>Generated: {new Date().toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore' })} {new Date().toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Singapore' })}</motion.div>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>)}
                    </>)}
                </motion.div>
            </Col>
            <Col md={6} className="d-flex justify-content-center align-items-center p-4">
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="w-100 bg-white rounded-4 shadow-sm p-4 p-md-5" style={{ maxWidth: "500px", border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-center mb-4">
                        <h3 className="fw-bold mb-3" style={{ color: '#1e293b', fontSize: '1.75rem' }}>{currentSection === 0 ? "Get Started" : "Create Your Account"}</h3>
                        <p className="text-muted" style={{ fontSize: '1rem' }}>{currentSection === 0 ? "Let's begin with some basic information" : `Complete all steps to register ${hasGuarantor ? "(with guarantor)" : ""}`}</p>
                    </motion.div>
                    {currentSection > 0 && (<div className="mb-4">
                        <div className="d-flex justify-content-between mb-2"><small>Step {currentSection} of {hasGuarantor ? 6 : 5}</small><small>{Math.round(completionPercentage())}% Complete</small></div>
                        <ProgressBar now={completionPercentage()} variant="primary" className="rounded-pill" style={{ height: '6px' }} />
                    </div>)}
                    <Form onSubmit={currentSection === (hasGuarantor ? 6 : 5) ? handleSubmit : handleNext}>{renderSection()}
                        <div className="d-flex justify-content-between mt-4">
                            {currentSection > 0 ? (<Button variant="outline-secondary" onClick={handlePrev} style={{ padding: '8px 20px', borderRadius: '0.5rem', fontSize: '14px' }}><FaChevronLeft className="me-1" /> Back</Button>) : (<div></div>)}
                            {currentSection < (hasGuarantor ? 6 : 5) ? (<motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}><Button variant="primary" type="submit" style={{ padding: '8px 20px', borderRadius: '0.5rem', background: 'linear-gradient(135deg, #4f46e5 0%,rgb(58, 61, 237) 100%)', border: 'none', fontSize: '14px' }}>Next <FaChevronRight className="ms-1" /></Button></motion.div>) : (currentSection > 0 && (<motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}><Button variant="primary" type="submit" style={{ padding: '0.75rem 1.5rem', borderRadius: '0.5rem', background: 'linear-gradient(135deg, #4f46e5 0%,rgb(58, 61, 237) 100%)', border: 'none' }} disabled={isSubmitting || staffAuthStatus !== true}>{isSubmitting ? (<><span className="spinner-border spinner-border-sm me-2"></span>Creating Account...</>) : (<><FaCheck className="me-2" /> Register</>)}</Button></motion.div>))}
                        </div>
                    </Form>
                    {currentSection > 0 && (<motion.div variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }} className="text-center mt-4"><p className="text-muted">Already have an account? <Link to="/sign-in" className="text-primary fw-bold text-decoration-none">Sign In</Link></p></motion.div>)}
                </motion.div>
            </Col>
            <ProductModal
                show={showProductModal}
                handleClose={() => setShowProductModal(false)}
                products={products}
                onSelect={(product) => handleProductSelect(product)}
                selectedProductId={formData.product_id}
            />
        </div>
    );
};

export default SignUp;